"use client";
import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";

interface ServicesContentProps {
  id: number;
  title: {
    number: string;
    mainTitle: string;
  };
  imageUrl: string;
  heading: string;
  description: string;
  keySpecifications: string[];
}

const ServicesContent = ({
  id,
  title,
  imageUrl,
  heading,
  description,
  keySpecifications,
}: ServicesContentProps) => {
  const isEvenType = id % 2 === 0;

  return (
    <motion.div
      id={title.mainTitle.toLowerCase().replace(/\s+/g, "-")}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="flex flex-col gap-8 md:gap-12 lg:justify-evenly py-[3rem] md:py-[6rem] px-[1.5rem] sm:px-[3rem] lg:px-[6rem]"
    >
      {/* Title Section */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: false }}
        className="flex flex-row gap-2 md:gap-4 text-gray-700 font-weight1 items-center leading-[.8]"
      >
        <div className="text-[60px] md:text-[140px]">{title.number}</div>
        <div className="sm:gap-2 md:gap-0 text-[40px] md:text-[90px] md:w-[80vw] lg:w-[60vw] flex flex-col sm:flex-row md:flex-col">
          <div className="">{title.mainTitle}</div>
        </div>
      </motion.div>

      <motion.div
        className={`flex flex-col md:flex-row gap-6 md:gap-[6vw] items-center ${
          isEvenType ? "md:flex-row-reverse" : ""
        }`}
      >
        {/* Image Container */}
        <div className="w-full md:w-[42vw] h-[200px] sm:h-[300px] md:h-[400px] relative">
          <Image
            src={imageUrl}
            alt={`${title.mainTitle} Image`}
            fill
            className="object-cover object-center"
          />
          {/* Contact Us Button */}
          <motion.a
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`flex flex-row gap-3 absolute bottom-0 ${
              isEvenType ? "left-0" : "right-0"
            } bg-white tracking-[.18em] py-2 px-4 text-color3 text-desc-size3 font-robo cursor-pointer hover:bg-[#40204A] hover:text-white`}
            href="#contact"
          >
            <span>Contact Us</span>
          </motion.a>
        </div>

        {/* Text Section */}
        <motion.div
          initial={{ opacity: 0, x: isEvenType ? -50 : -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="w-full md:w-[42vw] flex flex-col gap-4 mt-4 md:mt-0"
        >
          <div className="text-color1 text-desc-size1 md:text-title-size2 font-weight2 leading-[1]">
            {heading}
          </div>
          <div className="text-color3 text-desc-size2">{description}</div>
        </motion.div>
      </motion.div>

      {/* Key Specifications Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: false }}
        className="flex flex-col gap-4"
      >
        <div className="text-color1 text-desc-size1 font-weight2">
          Key Specifications :
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:gap-[2vw] w-full">
          {keySpecifications.map((spec, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: false }}
              className="px-4 py-4 bg-quad border border-terd text-desc-size2 rounded-md md:w-[32vw] flex items-center justify-center hover:bg-pent hover:border-none"
            >
              {spec}
            </motion.div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ServicesContent;
